/**
 * Time parsing utilities
 *
 * Ensures "8:00" and "08:00" normalize identically
 * for consistent validation and conflict checking.
 */

/**
 * Parse time string to minutes since midnight
 * Handles "HH:MM", "H:MM", and ISOString formats
 * Normalizes "8:00" and "08:00" identically
 */
export function parseMinutes(t: string | null | undefined): number {
  if (!t || typeof t !== 'string') return 0;

  // Handle ISOString format (e.g., "2024-01-15T14:30:00.000Z")
  if (t.includes('T')) {
    const dateObj = new Date(t);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.getUTCHours() * 60 + dateObj.getUTCMinutes();
    }
  }

  // Handle "HH:MM", "H:MM", or "HH:MM:SS" — ignore seconds when present
  const parts = t.split(':');
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!isNaN(h) && !isNaN(m)) {
      // Validate hours (0-23) and minutes (0-59)
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return h * 60 + m;
      }
    }
  }

  return 0;
}

/**
 * Normalize time string to "HH:MM" format for comparison
 * Ensures "8:00" becomes "08:00" for consistent string comparison
 */
export function normalizeTimeToHHMM(t: string | null | undefined): string {
  const minutes = parseMinutes(t);
  if (minutes === 0 && (!t || typeof t !== 'string')) return '00:00';

  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Minutes since midnight (can exceed 24h for edge cases) → HH:MM on a 24h clock. */
export function formatMinutesAsHHMM(totalMinutes: number): string {
  const normalized = ((Math.floor(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const CALENDAR_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const CALENDAR_DAY_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})/;
const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Parse a stored calendar day (`YYYY-MM-DD`) in the user's local timezone.
 * `new Date('YYYY-MM-DD')` alone is UTC midnight and shifts the displayed weekday in the Americas.
 * ISO timestamps (`…T00:00:00.000Z`) use the leading calendar date, not the UTC instant.
 */
export function parseCalendarDayLocal(dateStr: string): Date {
  const s = dateStr.trim();
  const day = s.match(CALENDAR_DAY_PREFIX_RE)?.[1];
  if (day) return new Date(`${day}T12:00:00`);
  return new Date(dateStr);
}

/** Display a stored calendar day without UTC off-by-one (`Sep 4` or `Sep 4, 2026`). */
export function formatCalendarDayLabel(
  dateStr: string | null | undefined,
  options?: { includeYear?: boolean }
): string {
  if (!dateStr?.trim()) return '';
  const d = parseCalendarDayLocal(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr.trim();
  const label = `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
  return options?.includeYear ? `${label}, ${d.getFullYear()}` : label;
}

/** `Sep 4 - Sep 7, 2026` from stored YYYY-MM-DD (or ISO) bounds. */
export function formatCalendarDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  emptyLabel = 'Dates TBD'
): string {
  const start = startDate?.trim() ? formatCalendarDayLabel(startDate) : '';
  const end = endDate?.trim() ? formatCalendarDayLabel(endDate, { includeYear: true }) : '';
  if (!start && !end) return emptyLabel;
  if (!start) return end;
  if (!end) return formatCalendarDayLabel(startDate, { includeYear: true });
  return `${start} - ${end}`;
}

/** Normalize any date string to `YYYY-MM-DD` for allocation/grid comparisons. */
export function normalizeCalendarDay(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const s = dateStr.trim();
  if (CALENDAR_DAY_RE.test(s)) return s;
  const isoDay = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) return isoDay[1];
  const parsed = parseCalendarDayLocal(s);
  if (!isNaN(parsed.getTime())) return formatCalendarDayLocal(parsed);
  return s.slice(0, 10);
}

/** Format a local Date as `YYYY-MM-DD` (avoids UTC drift from `toISOString()`). */
export function formatCalendarDayLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole calendar days from `now` (local midnight) until a stored tournament start day.
 * Returns null when the start date cannot be parsed.
 */
export function daysUntilCalendarDay(
  startDate: string | null | undefined,
  now: Date = new Date()
): number | null {
  if (!startDate?.trim()) return null;
  const start = parseCalendarDayLocal(startDate.trim());
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  return Math.round((start.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

/** Calendar day `YYYY-MM-DD` in an IANA zone, or the runtime local zone. */
export function calendarDayKey(now: Date = new Date(), timeZone?: string | null): string {
  const tz = timeZone?.trim();
  if (tz) {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now);
    } catch {
      /* fall through to local */
    }
  }
  return formatCalendarDayLocal(now);
}

/**
 * Which schedule day should start expanded.
 * During the event: today. Before first pitch: first day. After the last dated
 * slate (or a gap day): the next remaining day, else the last day.
 */
export function pickActiveScheduleDate(dateKeys: string[], todayKey: string): string | null {
  const dated = [...new Set(dateKeys.filter(key => key && key !== 'TBD'))].sort();
  if (dated.length === 0) {
    if (dateKeys.includes('TBD')) return 'TBD';
    return dateKeys[0] ?? null;
  }
  if (dated.includes(todayKey)) return todayKey;
  if (todayKey < dated[0]) return dated[0];
  const upcoming = dated.find(key => key >= todayKey);
  return upcoming ?? dated[dated.length - 1];
}

/** Local live window: 12:01 AM on start date through 11:59:59 PM on end date. */
function tournamentLiveWindow(startDate: string, endDate: string): { start: Date; end: Date } {
  const start = parseCalendarDayLocal(startDate);
  start.setHours(0, 1, 0, 0);

  const end = parseCalendarDayLocal(endDate);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Whether a tournament should appear as "Live Now" on public directory surfaces.
 * Uses {@link parseCalendarDayLocal} so `YYYY-MM-DD` bounds don't shift in US timezones.
 */
export function isTournamentLive(
  startDate: string,
  endDate: string,
  reference: Date = new Date()
): boolean {
  const { start, end } = tournamentLiveWindow(startDate, endDate);
  return reference >= start && reference <= end;
}

/** Whether a tournament has not yet entered its live window. */
export function isTournamentUpcoming(startDate: string, reference: Date = new Date()): boolean {
  const { start } = tournamentLiveWindow(startDate, startDate);
  return reference < start;
}

/** Inclusive list of calendar days between stored `YYYY-MM-DD` bounds, in local time. */
export function iterateCalendarDays(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const start = parseCalendarDayLocal(startDate);
  const end = parseCalendarDayLocal(endDate);
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(formatCalendarDayLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/**
 * Pick the best default day from a sorted list of `YYYY-MM-DD` keys:
 * today if games exist, else the next upcoming day, else the last day.
 */
export function pickDefaultCalendarDay(dates: string[], reference: Date = new Date()): string {
  if (dates.length === 0) return '';
  const today = formatCalendarDayLocal(reference);
  if (dates.includes(today)) return today;
  const upcoming = dates.find(d => d >= today);
  if (upcoming) return upcoming;
  return dates[dates.length - 1];
}

/**
 * Minimal structural shape of a game, for live-status calculation.
 * Accepts the app's {@link Game} type as well as lightweight projections.
 */
export interface GameLiveStatusInput {
  status?: string | null;
  date?: string | null;
  time?: string | null;
  durationMinutes?: number | null;
}

/**
 * Single source of truth for "is this game LIVE right now?".
 *
 * Rules:
 * - `in_progress` is always LIVE.
 * - `scheduled` is LIVE when the current time falls between game start and
 *   `durationMinutes` later (or 120 min if not set).
 * - Any other status (e.g. `completed`, `cancelled`) is never LIVE.
 *
 * Uses {@link parseCalendarDayLocal} + {@link parseMinutes} so calendar-only
 * dates don't shift timezone. All broadcast surfaces (scoreboard, schedule,
 * playoff bracket, jumbotron) must call this to stay in sync.
 */
/** Default slot length when `durationMinutes` is missing or invalid (matches live/score UX). */
export function getGameDurationMinutes(game: GameLiveStatusInput | null | undefined): number {
  if (game && typeof game.durationMinutes === 'number' && game.durationMinutes > 0) {
    return game.durationMinutes;
  }
  return 120;
}

/**
 * Scheduled local start time for a game, or null when date/time cannot be parsed.
 */
export function getGameScheduledStart(game: GameLiveStatusInput | null | undefined): Date | null {
  if (!game) return null;
  if (typeof game.date !== 'string' || game.date.length === 0) return null;
  if (typeof game.time !== 'string' || game.time.length === 0) return null;

  try {
    const gameDate = parseCalendarDayLocal(game.date);
    const timeMinutes = parseMinutes(game.time);
    const hours = Math.floor(timeMinutes / 60);
    const minutes = timeMinutes % 60;
    const gameStart = new Date(gameDate);
    gameStart.setHours(hours || 0, minutes || 0, 0, 0);
    return Number.isNaN(gameStart.getTime()) ? null : gameStart;
  } catch {
    return null;
  }
}

/** Scheduled local end time (start + duration), or null when start cannot be parsed. */
export function getGameScheduledEnd(game: GameLiveStatusInput | null | undefined): Date | null {
  const gameStart = getGameScheduledStart(game);
  if (!gameStart) return null;
  const durationMinutes = getGameDurationMinutes(game);
  return new Date(gameStart.getTime() + durationMinutes * 60 * 1000);
}

/**
 * Whether a game is past its scheduled end by at least `offsetMinutes` and still
 * needs a score (not completed or cancelled).
 */
export function isPastScoreFollowUpThreshold(
  game: GameLiveStatusInput | null | undefined,
  offsetMinutes = 20,
  now: Date = new Date()
): boolean {
  if (!game) return false;
  const status = (game.status || '').toLowerCase();
  if (
    status === 'completed' ||
    status === 'cancelled' ||
    status === 'delayed' ||
    status === 'disputed'
  )
    return false;

  const gameEnd = getGameScheduledEnd(game);
  if (!gameEnd) return false;

  const thresholdMs = gameEnd.getTime() + offsetMinutes * 60 * 1000;
  return now.getTime() >= thresholdMs;
}

export function isGameLive(game: GameLiveStatusInput | null | undefined): boolean {
  if (!game) return false;
  if (game.status === 'in_progress') return true;
  if (game.status !== 'scheduled') return false;

  const gameStart = getGameScheduledStart(game);
  const gameEnd = getGameScheduledEnd(game);
  if (!gameStart || !gameEnd) return false;

  const now = new Date();
  return now >= gameStart && now <= gameEnd;
}
